import { useResolvedTheme } from '@Core/hooks/useTheme';
import { Toaster as SonnerToaster, type ToasterProps } from 'sonner';

import { cn } from '@/lib/utils';

// Rich colors follow the Alert tints: body text stays on the popover foreground because
// --warning is tuned for solid fills and does not clear contrast as text on its own tint.
const richColorVariables = {
  '--success-bg': 'color-mix(in oklab, var(--success) 10%, var(--popover))',
  '--success-border': 'color-mix(in oklab, var(--success) 30%, var(--popover))',
  '--success-text': 'var(--popover-foreground)',
  '--info-bg': 'color-mix(in oklab, var(--informative) 10%, var(--popover))',
  '--info-border': 'color-mix(in oklab, var(--informative) 30%, var(--popover))',
  '--info-text': 'var(--popover-foreground)',
  '--warning-bg': 'color-mix(in oklab, var(--warning) 10%, var(--popover))',
  '--warning-border': 'color-mix(in oklab, var(--warning) 30%, var(--popover))',
  '--warning-text': 'var(--popover-foreground)',
  '--error-bg': 'color-mix(in oklab, var(--destructive) 10%, var(--popover))',
  '--error-border': 'color-mix(in oklab, var(--destructive) 30%, var(--popover))',
  '--error-text': 'var(--popover-foreground)'
};

const richColorClassNames = {
  success: '[&_[data-icon]]:text-success',
  info: '[&_[data-icon]]:text-informative',
  warning: '[&_[data-icon]]:text-warning',
  error: '[&_[data-icon]]:text-destructive'
};

// An open sheet puts its footer actions in the bottom corner, where bottom toasts land. Below `sm`
// the footer stacks its actions, so the toasts rise further. Sonner's own styles are unlayered,
// so only an important utility outranks them.
const clearSheetFooterClassName = [
  '[body:has([data-slot=sheet-footer])_&]:data-[y-position=bottom]:bottom-[calc(var(--offset-bottom)+4rem)]!',
  'max-sm:[body:has([data-slot=sheet-footer])_&]:data-[y-position=bottom]:bottom-[calc(var(--offset-bottom)+7rem)]!'
].join(' ');

function Toaster({ richColors, toastOptions, ...props }: ToasterProps) {
  const theme = useResolvedTheme();

  return (
    <SonnerToaster
      theme={theme}
      className={cn('toaster group', clearSheetFooterClassName)}
      richColors={richColors}
      toastOptions={
        richColors
          ? {
              ...toastOptions,
              classNames: { ...richColorClassNames, ...toastOptions?.classNames }
            }
          : toastOptions
      }
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
          ...richColorVariables
        } as React.CSSProperties
      }
      {...props}
    />
  );
}

export { Toaster };
